import { Injectable, NgZone, OnDestroy, Renderer2, inject } from '@angular/core';

import { AnchorType, Book, SpineItem } from '../models/epub';
import { TransformStore } from '@mhe/reader/state/transform';
import { anchorClick } from '../state/transform/transform.actions';
import { Next, Transformer } from './transformer';

@Injectable()
export class AnchorTransformer implements Transformer, OnDestroy {
  private readonly renderer = inject(Renderer2);
  private readonly zone = inject(NgZone);
  private readonly transformStore = inject(TransformStore);

  private unlisteners: Array<() => void> = [];
  private readonly frameStates = new Map<string, boolean>();

  async preRender(
    book: Book,
    spineItem: SpineItem,
    content: HTMLDocument,
    iframe: HTMLIFrameElement,
    next: Next,
  ): Promise<HTMLDocument> {
    // TODO(styles): This should go to a css selector
    Array.from(
      content.querySelectorAll<HTMLAnchorElement>(
        `a[*|type="${AnchorType.GLOSSARY}"]`,
      ),
    ).forEach((a) => (a.style.textDecoration = 'underline'));

    return await next(content);
  }

  async postRender(
    book: Book,
    spineItem: SpineItem,
    content: HTMLDocument,
    iframe: HTMLIFrameElement,
    next: Next,
  ): Promise<HTMLDocument> {
    if (iframe.id === 'clo-iframe') {
      this.resetUnlisteners();
      this.addAnchorListeners(content);
      return await next(content);
    }

    if (this.allFramesPresent()) {
      this.resetUnlisteners();
    }

    this.updateFrameState(iframe.id);

    this.addAnchorListeners(content);

    return await next(content);
  }

  private updateFrameState(frameId: string): void {
    if (frameId === 'right-iframe' || frameId === 'left-iframe') {
      this.frameStates.set(frameId, true);
    }
  }

  private allFramesPresent(): boolean {
    return this.frameStates.get('right-iframe') === true &&
           this.frameStates.get('left-iframe') === true;
  }

  private addAnchorListeners(content: HTMLDocument): void {
    const anchorElements = content.querySelectorAll('a');
    anchorElements.forEach((anchor) =>
      this.unlisteners.push(
        this.renderer.listen(
          anchor,
          'click',
          // Because this event comes from the iframe, which is not in the Angular zone, any code triggered from here is
          //   run outside the zone. In this case it was causing interpolation and other Angular features not to work in
          //   the large image component
          (event: MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            this.zone.run(() =>
              this.transformStore.dispatch(anchorClick({ anchor })),
            );
          },
        ),
      ),
    );
  }

  private resetUnlisteners(): void {
    this.unlisteners.forEach((unlistener) => unlistener());
    this.unlisteners = [];
    this.frameStates.clear();
  }

  ngOnDestroy(): void {
    this.resetUnlisteners();
  }
}
